import { useState, useCallback, useRef, useEffect } from "react"
import {
  Sidebar,
  SidebarInset,
  SidebarProvider,
} from "@/components/ui/sidebar"
import { Header } from "@/components/Header"
import { AppSidebar } from "@/components/AppSidebar"
import { MainContent, type MainContentHandle } from "@/components/MainContent"
import { ChatInput, type ChatInputHandle } from "@/components/ChatInput"
import type { ChatRequest } from "@/types/chatRequest"
import type { DttJurisdiction } from "@/types/jurisdictions"
import { getApiErrorMessage } from "@/lib/api"
import { streamChat } from "@/lib/chatStream"
import { createStreamSmoother } from "@/lib/streamSmoother"
import {
  AnalyticsEvents,
  timeEvent,
  trackEvent,
} from "@/lib/analytics"
import { useChatHistory } from "@/contexts/ChatHistoryContext"

export function Chat() {
  const {
    currentChatId,
    messages,
    addUserMessage,
    startAssistantMessage,
    appendAssistantToken,
    appendAssistantThinking,
    setAssistantStatus,
    finalizeAssistantMessage,
    cancelAssistantMessage,
    setChatId,
    loadHistory,
    setPendingNewChatTitle,
    setPendingChatId,
    submitFeedback,
    requestDeepDive,
    isDeepDivePending,
    deepDiveStatus,
    deepDiveError,
    newChatVersion,
    chatFromJurisdiction,
    chatSelectionVersion,
    syncChatJurisdiction,
  } = useChatHistory()
  // Every indexed Double Tax Treaty is Singapore ↔ a partner, so the destination
  // is fixed to Singapore (the home entity) and the user only picks the
  // counterpart source. A null source means a general (non-treaty) query.
  const HOME_JURISDICTION: DttJurisdiction = "Singapore"
  const [fromJurisdiction, setFromJurisdiction] =
    useState<DttJurisdiction | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [feedbackLoading, setFeedbackLoading] = useState(false)
  const chatInputRef = useRef<ChatInputHandle>(null)
  const mainContentRef = useRef<MainContentHandle>(null)

  useEffect(() => {
    if (newChatVersion > 0) {
      setFromJurisdiction(null)
    }
  }, [newChatVersion])

  useEffect(() => {
    if (chatSelectionVersion > 0) {
      setFromJurisdiction(chatFromJurisdiction)
    }
  }, [chatSelectionVersion, chatFromJurisdiction])

  // A counterpart entity turns the message into a treaty-scoped query (source →
  // Singapore). A general query needs nothing selected; a treaty query just
  // needs a counterpart distinct from Singapore (the dropdown disables Singapore
  // as a counterpart, so this holds whenever one is selected).
  const isTreatyQuery = fromJurisdiction !== null
  const canSend = !isTreatyQuery || fromJurisdiction !== HOME_JURISDICTION

  const handleSendMessage = useCallback(
    async (message: string) => {
      if (!canSend) {
        setError(
          "Pick a counterpart jurisdiction other than Singapore, or choose “No treaty”."
        )
        trackEvent(AnalyticsEvents.ChatMessageFailed, {
          reason: "invalid_entities",
          message_length: message.length,
        })
        return
      }
      setError(null)
      const isNewChat = !currentChatId
      addUserMessage(message)
      // Show the chat in the sidebar, titled, the moment the prompt is sent — not
      // after the reply. setPendingNewChatTitle creates the placeholder if none
      // exists (e.g. sending right after deleting the current chat), so it never
      // sits as "Untitled" or goes missing until the response.
      if (isNewChat) {
        setPendingNewChatTitle(message.slice(0, 100))
      }
      setTimeout(() => mainContentRef.current?.scrollToBottom(), 0)
      setLoading(true)
      timeEvent(AnalyticsEvents.ChatMessageSent)
      const streamSignal = startAssistantMessage()
      setTimeout(() => mainContentRef.current?.scrollToBottom(), 0)
      // Only send the jurisdiction pair (counterpart → Singapore) for a treaty
      // query; omitting both tells the backend to answer as a general query.
      const body: ChatRequest = {
        query: message,
        ...(currentChatId && { chat_id: currentChatId }),
        ...(isTreatyQuery && {
          from_jurisdiction: fromJurisdiction ?? undefined,
          to_jurisdiction: HOME_JURISDICTION,
        }),
      }
      // Tokens arrive in network-sized chunks; the smoothers re-emit them a
      // character at a time so the message doesn't stutter onto the screen.
      const tokenSmoother = createStreamSmoother(appendAssistantToken)
      const thinkingSmoother = createStreamSmoother(appendAssistantThinking)
      try {
        const data = await streamChat(body, {
          onMeta: ({ chat_id }) => {
            setChatId(chat_id)
          },
          onToken: (token) => {
            tokenSmoother.push(token)
          },
          onThinking: (token) => {
            thinkingSmoother.push(token)
          },
          onStatus: ({ text }) => {
            setAssistantStatus(text)
          },
        }, streamSignal)
        // Let the paced text finish rendering before the message is finalized —
        // finalize keeps whatever content has been appended so far.
        await Promise.all([thinkingSmoother.flush(), tokenSmoother.flush()])
        if (data.chat_id) {
          setChatId(data.chat_id)
        }
        // Sync scope before finalize, which ends the in-flight session that
        // syncChatJurisdiction inspects to know whether this chat is on screen.
        syncChatJurisdiction(isTreatyQuery ? fromJurisdiction : null)
        finalizeAssistantMessage(
          data.assistant_timestamp,
          data.sources,
          data.definitions
        )
        if (isNewChat) {
          if (data.chat_id) setPendingChatId(String(data.chat_id))
          loadHistory()
        }
        trackEvent(AnalyticsEvents.ChatMessageSent, {
          chat_id: data.chat_id,
          is_new_chat: isNewChat,
          from_jurisdiction: fromJurisdiction,
          to_jurisdiction: isTreatyQuery ? HOME_JURISDICTION : null,
          message_length: message.length,
          history_length: data.history_length,
          estimated_tokens: data.estimated_tokens,
        })
      } catch (e) {
        tokenSmoother.cancel()
        thinkingSmoother.cancel()
        // Aborted because the user deleted this chat mid-stream; there's
        // nothing left to show, so don't surface an error.
        if (streamSignal.aborted) return
        const wasOnScreen = cancelAssistantMessage()
        trackEvent(AnalyticsEvents.ChatMessageFailed, {
          from_jurisdiction: fromJurisdiction,
          to_jurisdiction: isTreatyQuery ? HOME_JURISDICTION : null,
          message_length: message.length,
          chat_id: currentChatId ?? undefined,
          error: getApiErrorMessage(e),
        })
        // Only show the error if the failed turn's chat is still on screen.
        if (wasOnScreen) setError(getApiErrorMessage(e))
      } finally {
        setLoading(false)
        chatInputRef.current?.focus()
      }
    },
    [
      currentChatId,
      canSend,
      isTreatyQuery,
      fromJurisdiction,
      addUserMessage,
      startAssistantMessage,
      appendAssistantToken,
      appendAssistantThinking,
      setAssistantStatus,
      finalizeAssistantMessage,
      cancelAssistantMessage,
      setChatId,
      loadHistory,
      setPendingNewChatTitle,
      setPendingChatId,
      syncChatJurisdiction,
    ]
  )

  const handleFeedback = useCallback(
    async (feedback: "up" | "down", assistantTimestamp?: string) => {
      setFeedbackLoading(true)
      await submitFeedback(feedback, assistantTimestamp)
      setFeedbackLoading(false)
    },
    [submitFeedback]
  )

  return (
    <SidebarProvider>
      <div className="flex h-dvh w-full">
        <Sidebar>
          <AppSidebar />
        </Sidebar>
        <SidebarInset className="min-h-0">
          <header className="flex h-14 shrink-0 items-center border-b px-4">
            <Header
              fromJurisdiction={fromJurisdiction}
              onFromChange={setFromJurisdiction}
            />
          </header>
          <div className="flex flex-1 min-h-0 flex-col overflow-hidden">
            {error && (
              <p className="text-destructive text-sm px-4 py-2 bg-destructive/10">
                {error}
              </p>
            )}
            <MainContent
              ref={mainContentRef}
              messages={messages}
              chatId={currentChatId}
              onFeedback={handleFeedback}
              feedbackDisabled={feedbackLoading || loading}
              onRequestDeepDive={requestDeepDive}
              isRequestingDeepDive={isDeepDivePending}
              deepDiveStatus={deepDiveStatus}
              deepDiveError={deepDiveError}
              onExampleMessage={handleSendMessage}
              examplesDisabled={loading || !canSend}
            />
          </div>
          <footer className="shrink-0 border-t p-4">
            <ChatInput
              ref={chatInputRef}
              onSubmit={handleSendMessage}
              loading={loading}
              disabled={loading || !canSend}
              disabledTitle={
                !canSend
                  ? "Pick a destination different from the source, or clear it"
                  : loading
                    ? "Sending message…"
                    : undefined
              }
            />
          </footer>
        </SidebarInset>
      </div>
    </SidebarProvider>
  )
}
